/**
 * Product copy + catalogs.
 *
 *   t('auth.login.signIn')
 *   t('catalogs.competencies.10.title')
 *   t('dashboard.quickStartTitle')
 *   get('help.analytics')
 *
 * Locale files export `{ en, de, fi }` with the same key tree.
 * Do not use English sentences as keys. Components import from this file,
 * not from catalogs/ or features/ directly.
 *
 * t()/get() follow the active UI locale (synced from LocaleProvider).
 * LocaleProvider overlays published Contentful copy on top of these files.
 * Missing locale values fall back to English at read time — do not copy English
 * into other locale files.
 */
import { DEFAULT_LOCALE, LOCALES } from '../locale/constants';
import { getActiveLocale } from './runtime';
import { dictionaries, localDictionaries } from './store';

import competencies from './catalogs/competencies';
import motivations from './catalogs/motivations';
import personality from './catalogs/personality';
import teamStrengths from './catalogs/teamStrengths';
import salesStrengths from './catalogs/salesStrengths';
import adept from './catalogs/adept';
import assessments from './catalogs/assessments';
import reports, { aliases as reportAliases } from './catalogs/reports';
import industries from './catalogs/industries';
import profiles from './catalogs/profiles';

import common from './ui/common';
import validation from './ui/validation';
import toasts from './ui/toasts';
import emptyStates from './ui/emptyStates';
import errors from './ui/errors';

import nav from './features/nav';
import auth from './features/auth';
import dashboard from './features/dashboard';
import teams from './features/teams';
import recruitment from './features/recruitment';
import talents from './features/talents';
import simulator from './features/simulator';
import analytics from './features/analytics';
import help from './features/help';
import account from './features/account';
import iam from './features/iam';

function pick(mod, locale) {
  return mod[locale] || mod.en;
}

function walk(dict, path) {
  return path.split('.').reduce(
    (node, key) => (node == null ? undefined : node[key]),
    dict
  );
}

export function buildDictionary(locale = DEFAULT_LOCALE) {
  return {
    catalogs: {
      competencies: pick(competencies, locale),
      motivations: pick(motivations, locale),
      personality: pick(personality, locale),
      teamStrengths: pick(teamStrengths, locale),
      salesStrengths: pick(salesStrengths, locale),
      adept: pick(adept, locale),
      assessments: pick(assessments, locale),
      reports: pick(reports, locale),
      industries: pick(industries, locale),
      profiles: pick(profiles, locale),
    },
    common: pick(common, locale),
    validation: pick(validation, locale),
    toasts: pick(toasts, locale),
    emptyStates: pick(emptyStates, locale),
    errors: pick(errors, locale),
    nav: pick(nav, locale),
    auth: pick(auth, locale),
    dashboard: pick(dashboard, locale),
    teams: pick(teams, locale),
    recruitment: pick(recruitment, locale),
    talents: pick(talents, locale),
    simulator: pick(simulator, locale),
    analytics: pick(analytics, locale),
    help: pick(help, locale),
    account: pick(account, locale),
    iam: pick(iam, locale),
    reportAliases: pick(reportAliases, locale),
  };
}

LOCALES.forEach((locale) => {
  const dict = buildDictionary(locale);
  localDictionaries[locale] = dict;
  dictionaries[locale] = JSON.parse(JSON.stringify(dict));
});

export function get(path, locale = getActiveLocale()) {
  const dict = dictionaries[locale] || dictionaries[DEFAULT_LOCALE];
  const value = walk(dict, path);
  if (value !== undefined) return value;
  if (locale !== DEFAULT_LOCALE) {
    return walk(dictionaries[DEFAULT_LOCALE], path);
  }
  return undefined;
}

export function t(path, vars = {}, locale = getActiveLocale()) {
  const value = get(path, locale);
  if (typeof value !== 'string') return path;
  return value.replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] == null ? `{${name}}` : String(vars[name])
  );
}

export { LOCALES, DEFAULT_LOCALE };
export { getActiveLocale, setActiveLocale } from './runtime';
export { companiesWithUserRolesManagement } from './config/flags';
export { dictionaries } from './store';
export {
  lookups,
  buildLookups,
  getLookups,
  clearLookupsCache,
  getIndustryTitle,
  localizeIndustryList,
  getProfileTitle,
  localizeProfileList,
} from './lookups';
